import { useApi } from "@/hooks/useApi";
import { CalendarIntegrationStatusDTO } from "@shaarp/common";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/** The status DTO the API returns — tokens deliberately absent. */
type GoogleCalendarIntegration = CalendarIntegrationStatusDTO;

type GoogleCalendarIntegrationContextType = {
  googleCalendarIntegration: GoogleCalendarIntegration | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

const GoogleCalendarIntegrationContext =
  createContext<GoogleCalendarIntegrationContextType>({
    googleCalendarIntegration: null,
    isLoading: true,
    refetch: async () => {},
  });

export const useGoogleCalendarIntegration =
  (): GoogleCalendarIntegrationContextType => {
    const context = useContext(GoogleCalendarIntegrationContext);
    if (!context) {
      throw new Error(
        "useGoogleCalendarIntegration must be used within a GoogleCalendarIntegrationProvider"
      );
    }
    return context;
  };

export const GoogleCalendarIntegrationProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const api = useApi();
  const [googleCalendarIntegration, setGoogleCalendarIntegration] =
    useState<GoogleCalendarIntegration | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchGoogleCalendarIntegration = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<GoogleCalendarIntegration>(
        "/integration/calendar/google-calendar"
      );
      setGoogleCalendarIntegration(data);
    } catch (error) {
      setGoogleCalendarIntegration(null);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const refetch = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    await fetchGoogleCalendarIntegration();
  }, [fetchGoogleCalendarIntegration]);

  useEffect(() => {
    fetchGoogleCalendarIntegration();
  }, [fetchGoogleCalendarIntegration]);

  const value = useMemo(
    () => ({
      googleCalendarIntegration,
      isLoading,
      refetch,
    }),
    [googleCalendarIntegration, isLoading, refetch],
  );

  return (
    <GoogleCalendarIntegrationContext.Provider value={value}>
      {children}
    </GoogleCalendarIntegrationContext.Provider>
  );
};
